/**
 * Система управления оружием в игре
 * Обрабатывает стрельбу, кулдауны и поведение снарядов
 */
import PoolManager from '../managers/PoolManager';
import {
  AreaAttack,
  BeamAttack,
  BoomerangAttack,
  ChainAttack,
  IWeaponBehavior,
  OrbitAttack,
  ProjectileAttack
} from './WeaponBehaviors';
import { PlayerStats } from '../components/PlayerStats';
import { AudioManager } from '../managers/AudioManager';
import { createLogger } from '../utils/Logger';
import { ContentRegistry, loadContentRegistry, WeaponBehaviorType, WeaponContent } from '../data';
import { AimVectorProvider, createWeaponRuntimeConfig, WeaponRuntimeConfig } from './WeaponTypes';
import type { DebugCommandFailureReason } from './DebugCommandTypes';

export type WeaponDefinition = WeaponContent;

interface ActiveWeapon {
  config: WeaponDefinition;
  level: number;
  runtimeConfig: WeaponRuntimeConfig;
  cooldownTimer: number; // Changed from lastFireTime
  behavior: IWeaponBehavior;
}

export interface WeaponUpgradeAttemptResult {
  readonly ok: boolean;
  readonly weaponId: string;
  readonly previousLevel: number;
  readonly currentLevel: number;
  readonly maxLevel: number;
  readonly reason?: Extract<DebugCommandFailureReason, 'unknown-weapon' | 'weapon-max-level' | 'execution-failed'>;
}

export interface ActiveWeaponSnapshot {
  id: string;
  name: string;
  nameKey?: string;
  level: number;
  maxLevel: number;
  icon: string;
  color?: number;
  config: { id: string };
}

/**
  * Класс системы оружия
  * Управляет стрельбой игрока, кулдаунами и созданием снарядов
  */
export class WeaponSystem {
  private player: any | null = null;
  private stats: PlayerStats | null = null;
  private activeWeapons: ActiveWeapon[] = [];
  private poolManager: PoolManager;
  private audioManager: AudioManager;
  private contentRegistry: ContentRegistry;
  private aimProvider: AimVectorProvider | null = null;
  private readonly logger = createLogger('WeaponSystem');

  constructor(poolManager: PoolManager, audioManager: AudioManager, contentRegistry: ContentRegistry = loadContentRegistry()) {
    this.poolManager = poolManager;
    this.audioManager = audioManager;
    this.contentRegistry = contentRegistry;
  }

  public init(player: any, stats?: PlayerStats): void {
    this.player = player;
    if (stats) this.stats = stats;
    this.logger.info('Initialized weapon system', { hasPlayer: !!player, hasStats: !!stats });
  }

  public setAimProvider(aimProvider: AimVectorProvider | null): void {
    this.aimProvider = aimProvider;
    this.logger.debug('Aim provider updated', { hasAimProvider: aimProvider !== null });
  }

  public getActiveWeapons(): ActiveWeaponSnapshot[] {
    return this.activeWeapons.map(w => ({
      id: w.config.id,
      name: w.config.name,
      nameKey: w.config.nameKey,
      config: { id: w.config.id },
      level: w.level,
      maxLevel: w.config.levels.length,
      icon: w.config.sprite,
      color: 0x2f5f9f
    }));
  }

  public unlockWeapon(weaponId: string): boolean {
    if (this.activeWeapons.find(w => w.config.id === weaponId)) {
      this.logger.warn('Weapon already unlocked', { weaponId });
      return false;
    }

    this.logger.debug('Weapon lookup for unlock', { weaponId });
    const def = this.contentRegistry.getWeapon(weaponId);
    if (!def) {
      this.logger.error('Unknown weapon unlock requested', { weaponId });
      return false;
    }

    const level1 = def.levels.find(l => l.level === 1) || def.levels[0];
    if (!level1) {
      this.logger.warn('Weapon has no level 1 definition', { weaponId });
      return false;
    }

    const runtimeConfig = createWeaponRuntimeConfig(def, level1, this.logger);
    if (!runtimeConfig) {
      this.logger.error('Failed to unlock weapon due to invalid runtime config', {
        weaponId,
        behavior: def.type,
        level: level1.level
      });
      return false;
    }

    this.logger.debug('Behavior selection requested', { weaponId, behavior: def.type });
    const behavior = this.createBehavior(def.type);
    if (!behavior) {
      this.logger.error('Unsupported weapon behavior type', { weaponId, behavior: def.type });
      return false;
    }

    const newWeapon: ActiveWeapon = {
      config: def,
      level: 1,
      runtimeConfig,
      cooldownTimer: 0, // Starts ready or at 0? 0 means it will count up to fireRate
      behavior
    };

    // Force first shot soon
    newWeapon.cooldownTimer = runtimeConfig.fireRate;

    this.activeWeapons.push(newWeapon);
    this.logger.info('Weapon unlocked', { weaponId, name: def.name, level: 1, behavior: def.type });
    return true;
  }

  public upgradeWeapon(weaponId: string): void {
    this.upgradeWeaponForTesting(weaponId);
  }

  public upgradeWeaponForTesting(weaponId: string): WeaponUpgradeAttemptResult {
    const weapon = this.activeWeapons.find(w => w.config.id === weaponId);
    if (!weapon) {
      this.logger.warn('upgradeWeaponForTesting:inactive-weapon', { weaponId });
      return {
        ok: false,
        weaponId,
        previousLevel: 0,
        currentLevel: 0,
        maxLevel: 0,
        reason: 'unknown-weapon'
      };
    }

    const previousLevel = weapon.level;
    const maxLevel = weapon.config.levels.length;
    const nextLevel = weapon.level + 1;
    const levelDef = weapon.config.levels.find(l => l.level === nextLevel);

    if (!levelDef) {
      this.logger.warn('upgradeWeaponForTesting:max-level-reached', { weaponId, currentLevel: weapon.level, maxLevel });
      return {
        ok: false,
        weaponId,
        previousLevel,
        currentLevel: weapon.level,
        maxLevel,
        reason: 'weapon-max-level'
      };
    }

    const runtimeConfig = createWeaponRuntimeConfig(weapon.config, levelDef, this.logger);
    if (!runtimeConfig) {
      this.logger.error('upgradeWeaponForTesting:invalid-runtime-config', {
        weaponId,
        level: nextLevel,
        behavior: weapon.config.type
      });
      return {
        ok: false,
        weaponId,
        previousLevel,
        currentLevel: weapon.level,
        maxLevel,
        reason: 'execution-failed'
      };
    }

    weapon.runtimeConfig = runtimeConfig;
    weapon.level = nextLevel;

    this.logger.info('Weapon upgraded', { weaponId, name: weapon.config.name, level: nextLevel });
    return {
      ok: true,
      weaponId,
      previousLevel,
      currentLevel: weapon.level,
      maxLevel
    };
  }

  public removeWeapon(weaponId: string): boolean {
      const index = this.activeWeapons.findIndex(w => w.config.id === weaponId);
      if (index !== -1) {
          this.activeWeapons[index]!.behavior.dispose?.(this.poolManager);
          this.activeWeapons.splice(index, 1);
          this.logger.info('Weapon removed', { weaponId });
          return true;
      }
      this.logger.warn('Weapon remove requested for inactive weapon', { weaponId });
      return false;
  }

  public dispose(): void {
    this.activeWeapons.forEach(weapon => weapon.behavior.dispose?.(this.poolManager));
    this.activeWeapons = [];
    this.logger.debug('dispose:complete');
  }

  private createBehavior(type: WeaponBehaviorType): IWeaponBehavior | null {
    switch (type) {
      case 'projectile':
        return new ProjectileAttack();
      case 'orbit':
        return new OrbitAttack();
      case 'area':
        return new AreaAttack();
      case 'beam':
        return new BeamAttack();
      case 'boomerang':
        return new BoomerangAttack();
      case 'chain':
        return new ChainAttack();
      default:
        return null;
    }
  }

  public update(dt: number): void {
    if (!this.player) return;

    this.activeWeapons.forEach(weapon => {
      // Accumulate cooldown timer
      weapon.cooldownTimer += dt / 1000;

      if (weapon.behavior.update) {
        weapon.behavior.update(
          dt / 1000,
          this.poolManager,
          this.player,
          weapon.runtimeConfig,
          weapon.level,
          this.stats || undefined,
          this.aimProvider || undefined
        );
      }
    });
  }

  public fire(): void {
    if (!this.player) return;

    this.activeWeapons.forEach(weapon => {
      const baseFireRate = weapon.runtimeConfig.fireRate;
      const cooldownMultiplier = this.stats?.get('cooldownMultiplier') ?? 1;
      const fireRate = baseFireRate * cooldownMultiplier;

      if (weapon.cooldownTimer >= fireRate) {
        // Reset timer, but keep remainder if any (optional, keeping it 0 is simpler and matches 'same time after each shot')
        weapon.cooldownTimer = 0;

        weapon.behavior.fire(
          this.poolManager,
          this.player,
          weapon.runtimeConfig,
          weapon.level,
          this.stats || undefined,
          this.aimProvider || undefined
        );

        if (weapon.config.type === 'projectile') {
          this.audioManager.playSfx('shoot', { volume: 0.5 });
        }
      }
    });
  }
}

export default WeaponSystem;
